import { Autocomplete, capitalize, Stack, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import femaleAudioFile from '../../../assets/voice_demos/female_voice_demo.mp3';
import maleAudioFile from '../../../assets/voice_demos/male_voice_demo.mp3';
import { useAgentRoles } from '../../../contexes/AgentRolesProvider';
import { TIMEZONE_OPTIONS } from '../../../constants/timezones';
import { useTranslation } from '../../../hooks/useTranslation';
import { AgentLanguage } from '../../../interfaces/Agent';
import StyledFormItem from '../../../styles/StyledFormItem';
import AutocompleteInput from '../../shared/AutoCompleteInput/AutoCompleteInput';
import ButtonIcon from '../../shared/ButtonIcon/ButtonIcon';
import Dropdown from '../../shared/DropDown/DropDown';
import PlayIcon from '../../shared/Icons/PlayIcon';
import StopIcon from '../../shared/Icons/StopIcon';
import Input from '../../shared/Input/Input';
import Text from '../../shared/Text/Text';
import WorkflowStepLabel from '../../shared/WorkflowStepLabel/WorkflowStepLabel';
import ResourceAccessFields from '../../shared/ResourceAccessFields/ResourceAccessFields';
import WorkingHoursPicker from '../../shared/WorkingHoursPicker/WorkingHoursPicker';
import HolidayListEditor from '../../shared/HolidayListEditor/HolidayListEditor';

interface AgentBehaviorStepProps {
  label?: string;
  stepLabel?: string;
  description?: string;
  agentVoice?: 'male' | 'female';
  showAccessFields?: boolean;
  ownerUserId?: string;
  isCreateMode?: boolean;
}

const AgentBehaviorStep: React.FC<AgentBehaviorStepProps> = ({
  agentVoice,
  label,
  stepLabel,
  description,
  showAccessFields = false,
  ownerUserId,
  isCreateMode = false,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioDemoRef = useRef<HTMLAudioElement | null>(null);
  const [voiceGender, setVoiceGender] = useState<'male' | 'female'>(
    agentVoice ?? 'female'
  );

  const { t } = useTranslation();
  const { roles: agentRoles, loading: rolesLoading } = useAgentRoles();

  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    if (!isCreateMode) return;
    if (getValues('time_zone')) return;

    const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isKnownZone = TIMEZONE_OPTIONS.some(
      option => option.value === browserZone
    );
    if (isKnownZone) {
      setValue('time_zone', browserZone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode]);

  useEffect(() => {
    if (isPlaying && audioDemoRef.current) {
      audioDemoRef.current.currentTime = 0;
      audioDemoRef.current.play();
    } else {
      audioDemoRef.current?.pause();
    }
  }, [isPlaying]);

  return (
    <div>
      <WorkflowStepLabel
        stepLabel={stepLabel}
        label={label ?? t('Agent Behavior')}
        description={description}
      />

      <StyledFormItem>
        <Text type="label">{t('Name')}</Text>
        <Input
          name="agent_name"
          required={true}
          withError={!!errors.agent_name}
          helperText={!!errors.agent_name ? t('Agent name is required') : ''}
        />
      </StyledFormItem>

      <StyledFormItem>
        <Text type="label">{t('Role')}</Text>

        <Dropdown
          name="role_id"
          required={true}
          options={
            rolesLoading
              ? []
              : agentRoles.map(agentRole => {
                  return {
                    label: agentRole.name,
                    value: agentRole.id,
                  };
                })
          }
          fullWidth
          withError={!!errors.role_id}
          helperText={
            rolesLoading
              ? `${t('Loading')}...`
              : !!errors.role_id
                ? t("Agent's role is required")
                : ''
          }
          disabled={rolesLoading}
        />
      </StyledFormItem>

      <StyledFormItem>
        <Text type="label">{t('Voice')}</Text>
        <Stack direction="row" position="relative">
          <Dropdown
            name="voice"
            required={true}
            options={[
              { label: t('Male'), value: 'male' },
              { label: t('Female'), value: 'female' },
            ]}
            fullWidth
            onChange={event =>
              setVoiceGender(event?.target.value as 'male' | 'female')
            }
            withError={!!errors.voice}
            helperText={!!errors.voice ? t("Agent's voice is required") : ''}
          />
          <ButtonIcon
            label={''}
            icon={isPlaying ? StopIcon : PlayIcon}
            onClick={() => setIsPlaying(!isPlaying)}
            position="absolute"
            top="28px"
            right="-64px"
            transform="translateY(-25px)"
          />
          <audio
            ref={audioDemoRef}
            src={voiceGender === 'female' ? femaleAudioFile : maleAudioFile}
            onEnded={() => setIsPlaying(!isPlaying)}
          ></audio>
        </Stack>
      </StyledFormItem>

      {/* NOTE: Curretly disabled since the agent picks up with the same sentence */}
      {/*
      <StyledFormItem>
        <Text type="label">Opening sentence</Text>
        <Input
          name="opening_sentence"
          required={true}
          withError={!!errors.opening_sentence}
          helperText={
            !!errors.opening_sentence
              ? "Agent's opening sentence is required"
              : ''
          }
        />
      </StyledFormItem>
      */}

      <StyledFormItem>
        <Text type="label">
          {t("Agent response if it doesn't know the answer")}
        </Text>
        <Input
          name="default_sentence"
          required={true}
          withError={!!errors.default_sentence}
          helperText={
            !!errors.default_sentence
              ? t("Agent's default sentence is required")
              : ''
          }
        />
      </StyledFormItem>

      <StyledFormItem>
        <Text type="label">{t('Data to collect')}</Text>
        <AutocompleteInput
          name="data_to_collect"
          options={[t('name'), t('email'), t('company')]}
          initialValue={[]}
          errorMessage={t('Data to collect item is required')}
        />
      </StyledFormItem>

      <StyledFormItem>
        <Text type="label">
          {t('Choose the default language of the agent')}
        </Text>

        <Dropdown
          name="default_language"
          required={true}
          options={[
            {
              label: capitalize(AgentLanguage.ENGLISH),
              value: AgentLanguage.ENGLISH,
            },
            {
              label: capitalize(AgentLanguage.FRENCH),
              value: AgentLanguage.FRENCH,
            },
            {
              label: capitalize(AgentLanguage.GERMAN),
              value: AgentLanguage.GERMAN,
            },
          ]}
          fullWidth
          withError={!!errors.default_language}
          helperText={
            !!errors.default_language
              ? t("Agent's default language is required")
              : ''
          }
        />
      </StyledFormItem>

      <StyledFormItem>
        <Text type="label">{t('Time Zone')}</Text>
        <Controller
          name="time_zone"
          control={control}
          render={({ field }) => (
            <Autocomplete
              options={TIMEZONE_OPTIONS}
              getOptionLabel={option => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              value={
                TIMEZONE_OPTIONS.find(option => option.value === field.value) ??
                null
              }
              onChange={(_, selected) => field.onChange(selected?.value ?? '')}
              onBlur={field.onBlur}
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  label={t('Time Zone')}
                  placeholder={t('Select a time zone')}
                />
              )}
            />
          )}
        />
      </StyledFormItem>

      <WorkingHoursPicker name="working_hours" />

      <HolidayListEditor name="holidays" />

      {showAccessFields && (
        <ResourceAccessFields
          ownerUserId={ownerUserId}
          resourceLabelKey="call agent"
        />
      )}
    </div>
  );
};

export default AgentBehaviorStep;
